/**
 * Theme store (Zustand) — the three mood-sibling Novera themes ported into Novanalyse:
 *   • pro    — flagship cool clinical white + navy chrome + luminous cyan (DEFAULT)
 *   • claire — bright airy daytime, light chrome
 *   • dark   — calm low-light, luminous cyan accent
 *
 * The chosen theme is written to <html data-theme=…> — which re-points every --nv-* semantic token
 * (tokens.css) without a rebuild — and persisted to localStorage under "nv.theme" (a non-sensitive
 * UX preference, in the same spirit as nv.locale / nv.density). EPHEMERAL UI state only; no server
 * data lives here.
 */
import { create } from "zustand";

export const THEMES = ["pro", "claire", "dark"] as const;
export type Theme = (typeof THEMES)[number];
export const DEFAULT_THEME: Theme = "pro";

const LS_THEME = "nv.theme";

function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}

function readStoredTheme(): Theme {
  if (typeof localStorage === "undefined") return DEFAULT_THEME;
  const v = localStorage.getItem(LS_THEME);
  return isTheme(v) ? v : DEFAULT_THEME;
}

/** Reflect the active theme onto <html data-theme> (the token switch). Idempotent + SSR-safe. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: readStoredTheme(),
  setTheme: (next) => {
    const theme = isTheme(next) ? next : DEFAULT_THEME;
    if (typeof localStorage !== "undefined") localStorage.setItem(LS_THEME, theme);
    applyTheme(theme);
    set({ theme });
  },
}));

/**
 * Apply the persisted theme to <html> at boot (call once from a top-level effect, alongside
 * applyDocumentLocale). Keeps a refresh on the chosen theme.
 */
export function initTheme(): void {
  applyTheme(readStoredTheme());
}
